import React, { Component } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { types as sdkTypes } from '../../util/sdkLoader';
import { REVIEW_TYPE_OF_PROVIDER, propTypes } from '../../util/types';
import { ensureUser } from '../../util/data';
import { withViewport } from '../../util/contextHelpers';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { loadData } from './ProfilePage.duck';
import { fetchMyTestimonials } from '../../custom-ducks/testimonials.duck';
import { fetchMyCredentials } from '../../custom-ducks/credentials.duck';
import { strToSlug } from '../../util/seo';
import ReactHtmlParser from 'react-html-parser';
import { decode } from 'html-entities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons';
import { faPlay, faTimes, faStar, faDesktop, faClock, faShareAlt } from '@fortawesome/free-solid-svg-icons';
import { getYoutubeEmbedUrl, getYoutubeThumbnail } from '../../util/gallery';
import { TIER_META, TIERS, readPublicBadgeTier } from '../../util/coachTier';
import TierBadge from '../../custom-components/elements/TierBadge/TierBadge';
import config from '../../config';

import css from './ProfilePage.css';
import PageWrapper from '../../custom-components/wrappers/page-wrapper/PageWrapper';
import ContainerWrapper from '../../custom-components/wrappers/container-wrapper/ContainerWrapper';
import { marketplaceLockupLogo as AppLogo } from '../../assets/viveka-logos';
import MainDashboardWrapper from '../../components/MainDashboardWrapper/MainDashboardWrapper';
import VerificationBadge from '../../custom-components/elements/verification-badge/VerificationBadge';
import PremiumBadge from '../../custom-components/elements/premium-badge/PremiumBadge';
import Footer from '../../custom-components/elements/footer/Footer';
import DefaultProfilePic from '../../assets/default_profile.png';
import specialtyClientLines from '../../config/specialtyClientLines.json';
import serviceCategoryClientLines from '../../config/serviceCategoryClientLines.json';

const { UUID } = sdkTypes;

const AVATAR_COLORS = [
  '#2a9d8f', '#e76f51', '#6d6875', '#457b9d', '#2b9348',
  '#e9c46a', '#264653', '#f4a261', '#b5838d', '#1d3557',
];

const CARD_GRADIENTS = [
  'linear-gradient(135deg, #0d4f3c 0%, #1a7a5e 100%)',
  'linear-gradient(135deg, #4c1d95 0%, #7c3aed 100%)',
  'linear-gradient(135deg, #78350f 0%, #d97706 100%)',
  'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
  'linear-gradient(135deg, #7f1d1d 0%, #ef4444 100%)',
  'linear-gradient(135deg, #14532d 0%, #16a34a 100%)',
];

function hashPick(str, arr) {
  let h = 0;
  for (let i = 0; i < (str || '').length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return arr[Math.abs(h) % arr.length];
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : (name || '').slice(0, 2).toUpperCase();
}

// Strips markup/entities down to plain, whitespace-collapsed text — used
// anywhere we need a short plain-text derivative of a rich-text field
// (bio one-liners, listing blurbs) rather than the full HTML render.
function stripHtml(html) {
  if (!html) return '';
  return decode(html).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

function truncate(text, maxLen) {
  if (!text || text.length <= maxLen) return text;
  const cut = text.slice(0, maxLen);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > 40 ? cut.slice(0, lastSpace) : cut).trim() + '…';
}

function firstSentence(text, maxLen) {
  if (!text) return '';
  const sentence = text.split(/(?:\.|!|\?)\s/)[0];
  return truncate(sentence, maxLen);
}

function listingUrl(listing) {
  return `/l/${strToSlug(listing.attributes.title || '')}/${listing.id.uuid}`;
}

function listingSpecialties(listing) {
  return (listing.attributes.publicData && listing.attributes.publicData['type-of-service']) || [];
}

function listingDeliveryModes(listing) {
  return (listing.attributes.publicData && listing.attributes.publicData['service-delivery-to']) || [];
}

// Coach-typed dates for a fixed-start offering (retreat, cohort, immersion) —
// blank for ongoing 1:1 coaching, so this is absent on most listings.
function listingEventDates(listing) {
  return (listing.attributes.publicData && listing.attributes.publicData['event-dates']) || '';
}

// Coaches set `per: 'hourly'` or leave it as a package price — mirrors the
// same field/label mapping used on marketplace search cards
// (ListingCardsWrapper.js) so pricing reads consistently across the app.
function perUnitLabel(listing) {
  return listing.attributes.publicData?.per === 'hourly' ? '/ hour' : '/ package';
}

function VideoModal({ embedUrl, onClose }) {
  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKeyUp = e => {
      if (e.keyCode === 27) onClose();
    };
    document.body.addEventListener('keyup', handleKeyUp);
    return () => {
      document.body.style.overflow = '';
      document.body.removeEventListener('keyup', handleKeyUp);
    };
  }, [onClose]);

  return (
    <div className={css.video_modal_backdrop} onClick={onClose}>
      <div className={css.video_modal_box} onClick={e => e.stopPropagation()}>
        <button
          type="button"
          className={css.video_modal_close}
          onClick={onClose}
          aria-label="Close video"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <iframe
          title="Video introduction"
          src={`${embedUrl}?autoplay=1`}
          className={css.video_iframe}
          allow="autoplay; encrypted-media"
          allowFullScreen="allowFullScreen"
          frameBorder="0"
        />
      </div>
    </div>
  );
}

function ProfileVideoIntro({ videoUrl }) {
  const [isModalOpen, setModalOpen] = React.useState(false);
  const embedUrl = getYoutubeEmbedUrl(videoUrl);
  if (!embedUrl) return null;

  return (
    <>
      <button
        type="button"
        className={css.video_thumb_wrap}
        onClick={() => setModalOpen(true)}
        aria-label="Play video introduction"
      >
        <img className={css.video_thumb_img} alt="Video introduction thumbnail" src={getYoutubeThumbnail(videoUrl)} />
        <span className={css.video_play_button}>
          <FontAwesomeIcon icon={faPlay} />
        </span>
      </button>
      {isModalOpen && <VideoModal embedUrl={embedUrl} onClose={() => setModalOpen(false)} />}
    </>
  );
}

// Coaches sometimes store an honorific ("Dr.", "Prof.") as part of their
// display name; taking the literal first token would show "About Dr."
// instead of the coach's actual first name.
const NAME_PREFIXES = ['dr', 'dr.', 'mr', 'mr.', 'mrs', 'mrs.', 'ms', 'ms.', 'prof', 'prof.', 'miss'];
function firstNameFrom(displayName) {
  const parts = (displayName || '').trim().split(/\s+/).filter(Boolean);
  const withoutPrefix = parts.length > 1 && NAME_PREFIXES.includes(parts[0].toLowerCase())
    ? parts.slice(1)
    : parts;
  return withoutPrefix[0] || 'Coach';
}

function StarRating({ rating }) {
  const r = Math.round(rating || 0);
  return (
    <span className={css.star_rating}>
      {Array.from({ length: 5 }, (_, i) => (
        <span key={i} className={i < r ? css.star_filled : css.star_empty}>★</span>
      ))}
    </span>
  );
}

export class ProfilePageComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      testimonials: [],
      credentials: [],
      bioExpanded: false,
      shareCopied: false,
      resultsExpanded: false,
    };
    this._fetchTestimonialsFor = null;
    this._fetchCredentialsFor = null;
    this.handleShareProfile = this.handleShareProfile.bind(this);
    this.toggleBio = this.toggleBio.bind(this);
    this.toggleResults = this.toggleResults.bind(this);
  }

  // Testimonials/credentials live in a separate coach-saas-service, not the
  // Sharetribe entities loaded via loadData. When viewing your own profile we
  // use the authenticated "my" endpoints (same ones the dashboard uses) so you
  // see everything you've submitted; for other coaches' profiles we use the
  // public endpoint, same split as ListingPage's sidebar.
  fetchTestimonials(coachId, isOwnProfile) {
    if (!coachId || this._fetchTestimonialsFor === coachId) return;
    this._fetchTestimonialsFor = coachId;

    if (isOwnProfile) {
      this.props.onFetchMyTestimonials()
        .then(data => {
          const confirmed = ((data && data.testimonials) || []).filter(t => t.status === 'confirmed');
          this.setState({ testimonials: confirmed });
        })
        .catch(() => {});
    } else {
      axios
        .get(`${config.coachSaaSService}testimonials/public/${coachId}?limit=10`)
        .then(res => {
          const list = Array.isArray(res.data) ? res.data : (res.data && res.data.testimonials) || [];
          this.setState({ testimonials: list });
        })
        .catch(() => {});
    }
  }

  fetchCredentials(coachId, isOwnProfile) {
    if (!coachId || this._fetchCredentialsFor === coachId) return;
    this._fetchCredentialsFor = coachId;

    if (isOwnProfile) {
      this.props.onFetchMyCredentials()
        .then(data => {
          const approved = ((data && data.credentials) || []).filter(c => c.status === 'approved');
          this.setState({ credentials: approved });
        })
        .catch(() => {});
    } else {
      axios
        .get(`${config.coachSaaSService}credentials/public/${coachId}?limit=20`)
        .then(res => {
          const list = (res.data && res.data.credentials) || [];
          this.setState({ credentials: list });
        })
        .catch(() => {});
    }
  }

  checkAndFetch() {
    const { user, currentUser } = this.props;
    if (!user || !user.id) return;
    const coachId = user.id.uuid;
    const isOwnProfile = !!(currentUser && currentUser.id && currentUser.id.uuid === coachId);
    this.fetchTestimonials(coachId, isOwnProfile);
    this.fetchCredentials(coachId, isOwnProfile);
  }

  componentDidMount() {
    this.checkAndFetch();
  }

  componentDidUpdate() {
    this.checkAndFetch();
  }

  handleShareProfile() {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    if (!url || !navigator.clipboard) return;
    navigator.clipboard.writeText(url)
      .then(() => {
        this.setState({ shareCopied: true });
        setTimeout(() => this.setState({ shareCopied: false }), 2000);
      })
      .catch(() => {});
  }

  toggleBio() {
    this.setState(s => ({ bioExpanded: !s.bioExpanded }));
  }

  toggleResults() {
    this.setState(s => ({ resultsExpanded: !s.resultsExpanded }));
  }

  render() {
    const { user, listings, reviews, currentUser } = this.props;
    const { testimonials, bioExpanded, shareCopied, resultsExpanded } = this.state;
    const isLoggedIn = !!currentUser;

    const profileUser = ensureUser(user);
    const displayName = profileUser.attributes.profile.displayName || '';
    const bio = profileUser.attributes.profile.bio || '';
    const publicData = profileUser.attributes.profile.publicData || {};
    const metadata = profileUser.attributes.profile.metadata || {};

    const location = publicData.location || '';
    const credentials = this.state.credentials.length
      ? this.state.credentials
      : (Array.isArray(publicData.credentials) ? publicData.credentials : []);
    const videoIntroUrl = publicData.videoIntroUrl || '';
    const methodologyName = publicData.methodologyName || '';
    const methodologyTagline = publicData.methodologyTagline || '';
    const methodologyDescription = publicData.methodologyDescription || '';
    const replyTimeHours = publicData.replyTimeHours || null;

    let profileImage = '';
    if (profileUser.profileImage) {
      try {
        profileImage = profileUser.profileImage.attributes.variants['scaled-large'].url;
      } catch (_) {}
    }

    // While the user entity (and its profile image) is still being fetched,
    // avoid flashing the "?" initials placeholder — show a neutral skeleton
    // instead until we actually know whether this coach has a photo.
    const isUserLoading = !user || !user.id;

    // Aggregate specialties from listings' type-of-service (hyphenated key —
    // this is how Services.js actually writes it into publicData).
    const allSpecialties = listings.reduce((acc, l) => {
      listingSpecialties(l).forEach(s => { if (!acc.includes(s)) acc.push(s); });
      return acc;
    }, Array.isArray(publicData.specialties) ? [...publicData.specialties] : []);

    const tagline = publicData.tagline || publicData.jobTitle || publicData.title
      || allSpecialties.slice(0, 3).join(' · ');

    // Aggregate unique delivery modes ("In person", "Virtual") across all
    // listings so the hero can show one combined line rather than one per
    // offering.
    const allDeliveryModes = listings.reduce((acc, l) => {
      listingDeliveryModes(l).forEach(d => { if (!acc.includes(d)) acc.push(d); });
      return acc;
    }, []);
    const deliveryModesLabel = allDeliveryModes.join(' & ');

    const bioPlain = stripHtml(bio);
    const heroSummary = bioPlain ? truncate(bioPlain, 170) : '';
    const isBioLong = bioPlain.length > 480;

    // Stats
    const providerReviews = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);

    const firstListing = listings[0] || null;
    const introListing = listings.find(l => (l.attributes.price?.amount || 0) === 0)
      || listings.find(l => /intro|free/i.test(l.attributes.title || ''))
      || null;

    const firstName = firstNameFrom(displayName);

    const coachSinceYear = profileUser.attributes.createdAt
      ? new Date(profileUser.attributes.createdAt).getFullYear()
      : null;

    const publicBadgeTier = readPublicBadgeTier(profileUser);
    const isVerifiedTier = publicBadgeTier !== TIERS.NETWORK_MEMBER;
    const tierMeta = TIER_META[publicBadgeTier];

    const resultsCount = testimonials.length + providerReviews.length;

    // ── Client results: testimonials (verified badge) + marketplace reviews
    // (star rating) normalised into one shape so they share a single grid
    // and a single "read all" cutoff instead of two separately-capped lists.
    const resultItems = [
      ...testimonials.map((t, i) => ({
        key: t.id || `t-${i}`,
        quote: t.body || '',
        name: t.refereeName || 'Client',
        sub: [t.refereeTitle, t.refereeOrg].filter(Boolean).join(' · '),
        verified: true,
        linkedInUrl: t.refereeLinkedInUrl || '',
      })),
      ...providerReviews.map((r, i) => ({
        key: r.id?.uuid || `r-${i}`,
        quote: r.attributes.content || '',
        name: r.author?.attributes?.profile?.displayName || 'Client',
        sub: '',
        rating: r.attributes.rating || 5,
      })),
    ];
    const RESULTS_PREVIEW_COUNT = 4;
    const visibleResultItems = resultsExpanded ? resultItems : resultItems.slice(0, RESULTS_PREVIEW_COUNT);
    const hasMoreResults = resultItems.length > RESULTS_PREVIEW_COUNT;

    // ── Stats bar tiles: only render what's backed by real data. ──
    const statsTiles = [];
    if (allSpecialties[0]) {
      statsTiles.push({ value: allSpecialties[0], label: 'Primary practice' });
    }
    if (isVerifiedTier) {
      statsTiles.push({ value: tierMeta.label, label: 'Viveka status · earned' });
    }
    if (credentials.length) {
      const top = credentials[0];
      const org = top.issuingOrg || top.organization || '';
      statsTiles.push({ value: top.title, label: org ? `${org} · verified` : 'Verified' });
    }
    if (resultsCount) {
      statsTiles.push({
        value: String(resultsCount),
        label: resultsCount === 1 ? 'Client result' : 'Client results',
      });
    }
    if (publicData.yearsExperience) {
      statsTiles.push({ value: `${publicData.yearsExperience}+ years`, label: 'In practice' });
    }
    if (methodologyName) {
      statsTiles.push({ value: 'Own method', label: methodologyTagline || methodologyName });
    }

    // ── "How I can help" cards: top specialties, each paired with a real
    // sentence pulled from a listing tagged with that specialty (no
    // invented copy when nothing matches). A coach with few listings spread
    // across many specialties would otherwise show the same sentence under
    // every card — only the first specialty to claim a given listing gets
    // its description; the rest show the title alone. ──
    //
    // allSpecialties starts with the coach's profile-level specialties
    // (which may not be backed by any actual listing) before appending
    // listing type-of-service tags — so a naive slice(0, 3) can pick 3
    // profile-only specialties and leave every card copy-less even when
    // listing descriptions exist for other specialties further down the
    // list. Prioritise specialties that DO have a matching listing so the
    // cards most likely to have real copy are the ones shown.
    const listingBackedSpecialties = listings.reduce((acc, l) => {
      listingSpecialties(l).forEach(s => { if (!acc.includes(s)) acc.push(s); });
      return acc;
    }, []);
    const helpCardSpecialties = [
      ...allSpecialties.filter(s => listingBackedSpecialties.includes(s)),
      ...allSpecialties.filter(s => !listingBackedSpecialties.includes(s)),
    ];

    const usedListingIds = new Set();
    const helpCards = helpCardSpecialties.slice(0, 3).map(spec => {
      const related = listings.find(l => listingSpecialties(l).includes(spec) && !usedListingIds.has(l.id.uuid));
      let desc = '';
      if (related) {
        usedListingIds.add(related.id.uuid);
        desc = firstSentence(stripHtml(related.attributes.description || ''), 140);
      } else if (specialtyClientLines[spec] || serviceCategoryClientLines[spec]) {
        // No listing is tagged with this specialty — use the tag's own
        // second-person client line from the taxonomy instead of repeating
        // the coach's bio under every untagged specialty. Falls back to
        // serviceCategoryClientLines for the coarser top-level `services`
        // categories (marketplace-custom-config.js), which aren't part of
        // the fine-grained specialty taxonomy.
        desc = specialtyClientLines[spec] || serviceCategoryClientLines[spec];
      } else if (heroSummary) {
        // Specialty isn't in the taxonomy either (e.g. a legacy tag) —
        // fall back to the coach's own bio rather than leaving the card
        // copy-less. Still real copy, just not specialty-specific.
        desc = heroSummary;
      }
      return { spec, desc };
    });
    const hasHelpSection = helpCards.length > 0 || (videoIntroUrl && getYoutubeEmbedUrl(videoIntroUrl));

    const pageSchema = {
      '@context': 'http://schema.org',
      '@type': 'ProfilePage',
      name: `${displayName} | Viveka`,
      image: profileImage,
    };
    const pageDescription = bioPlain ? bioPlain.substring(0, 250) : '';

    // Shared hero + body content, styled slightly differently inside dashboard
    const innerContent = (
      <>
        {/* ═══════════════ HERO ═══════════════ */}
        <div
          className={css.hero_wrap}
          style={isLoggedIn ? { borderRadius: '14px', marginBottom: '16px', overflow: 'hidden' } : undefined}
        >
          <div className={css.hero}>
            <div className={css.hero_inner}>
              <div className={css.hero_photo_col}>
                <div className={css.hero_photo}>
                  <div className={css.hero_photo_circle}>
                    {profileImage
                      ? <img className={css.hero_img} src={profileImage} alt={displayName} />
                      : isUserLoading
                      ? <div className={css.hero_photo_skeleton} />
                      : <div className={css.hero_initials_bg}>
                          <span className={css.hero_initials}>{getInitials(displayName)}</span>
                        </div>
                    }
                  </div>
                </div>
                {(isVerifiedTier || coachSinceYear) && (
                  <div className={css.hero_photo_caption}>
                    {isVerifiedTier
                      ? <TierBadge tier={publicBadgeTier} size="sm" showLabel />
                      : <span />
                    }
                    {coachSinceYear && (
                      <span className={css.hero_coach_since}>Coach since {coachSinceYear}</span>
                    )}
                  </div>
                )}
              </div>
              <div className={css.hero_info}>
                {(testimonials.length > 0 || isVerifiedTier) && (
                  <div className={css.hero_badges_row}>
                    {testimonials.length > 0 && (
                      <span className={css.hero_badge_pill}>
                        {testimonials.length} verified testimonial{testimonials.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    {isVerifiedTier && (
                      <TierBadge tier={publicBadgeTier} size="md" showLabel />
                    )}
                  </div>
                )}
                <div className={css.hero_name_row}>
                  <h1 className={css.hero_name}>{displayName}</h1>
                  <VerificationBadge user={profileUser} size="lg" variant="diamond" />
                  {metadata.premium && <PremiumBadge />}
                </div>
                {tagline && <p className={css.hero_tagline}>{tagline}</p>}
                {heroSummary && <p className={css.hero_summary_text}>{heroSummary}</p>}
                {(location || deliveryModesLabel || publicData.yearsExperience) && (
                  <>
                    <span className={css.hero_divider} />
                    <p className={css.hero_meta_row}>
                      {location && (
                        <span className={css.hero_meta_item}>
                          <svg className={css.hero_meta_icon} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" /></svg>
                          {location}
                        </span>
                      )}
                      {deliveryModesLabel && (
                        <>
                          {location && <span className={css.hero_meta_dot}>·</span>}
                          <span className={css.hero_meta_item}>
                            <FontAwesomeIcon icon={faDesktop} className={css.hero_meta_icon} />
                            {deliveryModesLabel}
                          </span>
                        </>
                      )}
                      {publicData.yearsExperience && (
                        <>
                          {(location || deliveryModesLabel) && <span className={css.hero_meta_dot}>·</span>}
                          <span className={css.hero_meta_item}>
                            <FontAwesomeIcon icon={faClock} className={css.hero_meta_icon} />
                            {publicData.yearsExperience}+ years
                          </span>
                        </>
                      )}
                    </p>
                  </>
                )}
              </div>

              {/* ── Booking card, sticky in the hero on the right ── */}
              {firstListing && (
                <div className={css.next_card_col}>
                  <div className={css.next_card}>
                    <p className={css.next_card_label}>Next experience</p>
                    <h3 className={css.next_card_title}>{firstListing.attributes.title}</h3>
                    {(listingSpecialties(firstListing)[0] || listingDeliveryModes(firstListing)[0] || location) && (
                      <p className={css.next_card_meta}>
                        {[listingSpecialties(firstListing)[0], listingDeliveryModes(firstListing)[0], location].filter(Boolean).join(' · ')}
                      </p>
                    )}
                    {firstListing.attributes.price && (
                      <p className={css.next_card_price}>
                        ${(firstListing.attributes.price.amount / 100).toLocaleString()}
                        <span>{perUnitLabel(firstListing)}</span>
                      </p>
                    )}
                    <Link to={listingUrl(firstListing)} className={css.next_card_primary}>
                      Book a session
                    </Link>
                    {introListing && introListing.id.uuid !== firstListing.id.uuid && (
                      <Link to={listingUrl(introListing)} className={css.next_card_secondary}>
                        Schedule free intro call
                      </Link>
                    )}
                    {(listingEventDates(firstListing) || testimonials.length > 0 || replyTimeHours || (introListing && (introListing.attributes.price?.amount || 0) === 0)) && (
                      <div className={css.next_card_footer}>
                        {listingEventDates(firstListing) && (
                          <span className={css.next_card_footnote}>📅 Next dates: {listingEventDates(firstListing)}</span>
                        )}
                        {introListing && (introListing.attributes.price?.amount || 0) === 0 && (
                          <span className={css.next_card_footnote}>✓ No card required for the intro call</span>
                        )}
                        {testimonials.length > 0 && (
                          <span className={css.next_card_footnote}>
                            {testimonials.length} verified testimonial{testimonials.length !== 1 ? 's' : ''}
                          </span>
                        )}
                        {replyTimeHours && (
                          <span className={css.next_card_footnote}>Usually replies within {replyTimeHours} hours</span>
                        )}
                      </div>
                    )}
                  </div>
                  <button type="button" className={css.share_btn} onClick={this.handleShareProfile}>
                    <FontAwesomeIcon icon={faShareAlt} />
                    {shareCopied ? 'Link copied' : 'Share this profile'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ═══════════════ STATS BAR ═══════════════ */}
          {statsTiles.length >= 2 && (
            <div className={css.stats_bar}>
              {statsTiles.slice(0, 5).map((tile, i) => (
                <div key={i} className={css.stats_tile}>
                  <p className={css.stats_tile_value}>{tile.value}</p>
                  <p className={css.stats_tile_label}>{tile.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ═══════════════ BODY ═══════════════ */}
        <div
          className={css.body_outer}
          style={isLoggedIn ? { borderRadius: '14px' } : undefined}
        >
          <div className={css.body_inner}>
            {/* ── How I can help ── */}
            {hasHelpSection && (
              <section className={css.section}>
                <div className={css.section_header}>
                  <h2 className={css.section_title}>How {firstName} can help</h2>
                  
                </div>
                <div className={css.help_layout}>
                  {helpCards.length > 0 && (
                    <div className={css.help_cards}>
                      {helpCards.map(({ spec, desc }) => (
                        <div key={spec} className={css.help_card}>
                          <span className={css.help_card_accent} />
                          <h3 className={css.help_card_title}>{spec}</h3>
                          {desc && <p className={css.help_card_desc}>{desc}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                  {videoIntroUrl && getYoutubeEmbedUrl(videoIntroUrl) && (
                    <div className={css.help_video_col}>
                      <div className={css.help_video_card}>
                        <ProfileVideoIntro videoUrl={videoIntroUrl} />
                        <p className={css.help_video_caption}>Meet {firstName}</p>
                      </div>
                      {heroSummary && <p className={css.help_video_desc}>{heroSummary}</p>}
                      {firstListing && (
                        <Link to={listingUrl(firstListing)} className={css.help_video_cta}>
                          Book a session
                        </Link>
                      )}
                      {introListing && introListing.id.uuid !== firstListing?.id.uuid && (
                        <Link to={listingUrl(introListing)} className={css.help_video_subcta}>
                          or schedule a free intro call
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* ── Ways to work with {firstName} ── */}
            {listings.length > 0 && (
              <section id="offerings-section" className={css.section}>
                <div className={css.section_header}>
                  <h2 className={css.section_title}>Ways to work with {firstName}</h2>
                  <span className={css.section_meta}>{listings.length} offering{listings.length !== 1 ? 's' : ''}</span>
                </div>
                <div className={css.offer_grid}>
                  {listings.map((listing, i) => {
                    const lTitle = listing.attributes.title || '';
                    const lPrice = (listing.attributes.price?.amount || 0) / 100;
                    const lDesc = truncate(stripHtml(listing.attributes.description || ''), 140);
                    const lImage = listing.images?.[0]?.attributes?.variants?.['landscape-crop']?.url || '';
                    const lTypes = listingSpecialties(listing);
                    const lDates = listingEventDates(listing);
                    const gradient = hashPick(lTitle || String(i), CARD_GRADIENTS);
                    return (
                      <Link key={listing.id.uuid} to={listingUrl(listing)} className={css.offer_card}>
                        <div className={css.offer_image} style={lImage ? undefined : { background: gradient }}>
                          {lImage
                            ? <img src={lImage} alt={lTitle} className={css.offer_img} />
                            : <span className={css.offer_initials}>{getInitials(lTitle)}</span>
                          }
                          {lTypes[0] && <span className={css.offer_badge}>{lTypes[0].toUpperCase()}</span>}
                        </div>
                        <div className={css.offer_body}>
                          <p className={css.offer_title}>{lTitle}</p>
                          {lDates && <p className={css.offer_dates}>📅 {lDates}</p>}
                          {lDesc && <p className={css.offer_desc}>{lDesc}</p>}
                          <div className={css.offer_footer}>
                            <span className={css.offer_price}>
                              {lPrice ? `$${lPrice.toLocaleString()}` : 'Free'}
                              {lPrice ? <small>{perUnitLabel(listing)}</small> : null}
                            </span>
                            <span className={css.offer_link}>Explore experience →</span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </section>
            )}

            {/* ── Meet {firstName} ── */}
            {bio && (
              <section className={css.section}>
                <div className={css.section_header}>
                  <h2 className={css.section_title}>Meet {firstName}</h2>
                </div>
                <div className={[
                  'ql-editor',
                  css.bio_text,
                  isBioLong && !bioExpanded ? css.bio_clamped : '',
                ].join(' ')}>
                  {ReactHtmlParser(decode(bio))}
                </div>
                {isBioLong && (
                  <button type="button" className={css.bio_toggle} onClick={this.toggleBio}>
                    {bioExpanded ? 'Show less' : `Read ${firstName}'s full story`}
                  </button>
                )}
                {methodologyName && (
                  <div className={css.methodology_box}>
                    <p className={css.methodology_title}>
                      {methodologyName}{methodologyTagline ? ` — ${methodologyTagline}` : ''}
                    </p>
                    {methodologyDescription && (
                      <p className={css.methodology_desc}>{methodologyDescription}</p>
                    )}
                  </div>
                )}
              </section>
            )}

            {/* ── Client results ── */}
            {resultItems.length > 0 && (
              <section id="reviews-section" className={css.section}>
                <div className={css.section_header}>
                  <h2 className={css.section_title}>Client Testimonials</h2>
                  <span className={css.section_meta}>
                    {resultsCount} verified
                    {hasMoreResults && (
                      <>
                        {' · '}
                        <button type="button" className={css.read_all_btn} onClick={this.toggleResults}>
                          {resultsExpanded ? 'show less' : 'read all'}
                        </button>
                      </>
                    )}
                  </span>
                </div>
                <div className={css.results_grid}>
                  {visibleResultItems.map(item => (
                    <div key={item.key} className={css.result_card}>
                      {item.quote && <p className={css.result_quote}>&ldquo;{item.quote}&rdquo;</p>}
                      <div className={css.result_footer}>
                        <span className={css.review_avatar} style={{ background: hashPick(item.name, AVATAR_COLORS) }}>
                          {getInitials(item.name)}
                        </span>
                        <div className={css.review_meta}>
                          <span className={css.review_name}>{item.name}</span>
                          {item.sub && <span className={css.review_date}>{item.sub}</span>}
                        </div>
                        {item.verified
                          ? <span className={css.testimonial_verified}>✓ Verified</span>
                          : <StarRating rating={item.rating} />
                        }
                      </div>
                      {item.linkedInUrl && (
                        <a
                          href={item.linkedInUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={css.testimonial_linkedin}
                        >
                          <FontAwesomeIcon icon={faLinkedin} />
                          View on LinkedIn
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* ── Credentials & experience ── */}
            {credentials.length > 0 && (
              <section id="credentials-section" className={css.section}>
                <div className={css.section_header}>
                  <h2 className={css.section_title}>Credentials &amp; experience</h2>
                  <span className={css.section_meta}>Résumé material last, not first</span>
                </div>
                <div className={css.credentials_grid}>
                  {credentials.map((c, i) => {
                    const org = c.issuingOrg || c.organization || '';
                    const year = c.issuedYear || c.year || '';
                    return (
                      <div key={c.id || i} className={css.credential_item}>
                        {year && <span className={css.credential_year}>{year}</span>}
                        <div className={css.credential_content}>
                          <p className={css.credential_title}>{c.title}</p>
                          {org && <p className={css.credential_sub}>{org}</p>}
                          <div className={css.credential_footer}>
                            <span className={css.testimonial_verified}>✓ Verified</span>
                            {c.fileUrl && (
                              <a href={c.fileUrl} target="_blank" rel="noopener noreferrer" className={css.credential_link}>
                                View certificate ↗
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        </div>

        {/* ═══════════════ CLOSING CTA ═══════════════ */}
        {firstListing && (
          <div
            className={css.closing_cta}
            style={isLoggedIn ? { borderRadius: '14px', marginTop: '16px', overflow: 'hidden' } : undefined}
          >
            <div className={css.closing_cta_inner}>
              <div>
                <h2 className={css.closing_cta_title}>Start a conversation with {firstName}</h2>
                <p className={css.closing_cta_sub}>
                  {introListing
                    ? "A short intro call to describe what's going on — no card required."
                    : 'Reach out to find the right way to work together.'}
                </p>
              </div>
              <div className={css.closing_cta_actions}>
                <Link
                  to={listingUrl(introListing || firstListing)}
                  className={css.closing_cta_primary}
                >
                  {introListing ? 'Schedule free intro call' : 'Book a session'}
                </Link>
                <a href="#offerings-section" className={css.closing_cta_secondary}>
                  See all ways to work with {firstName}
                </a>
              </div>
            </div>
          </div>
        )}
      </>
    );

    // ── Logged-in: use the dashboard wrapper (same sidebar + navbar as every other page) ──
    if (isLoggedIn) {
      return (
        <PageWrapper title={`${displayName} | Viveka`} image={profileImage} className={css.page_wrapper} description={pageDescription} schema={pageSchema}>
          <MainDashboardWrapper title={displayName || 'Coach Profile'} page="profile-view">
            {innerContent}
          </MainDashboardWrapper>
        </PageWrapper>
      );
    }

    // ── Logged-out: public nav matching the search page ──
    return (
      <PageWrapper title={`${displayName} | Viveka`} image={profileImage} className={css.page_wrapper} description={pageDescription} schema={pageSchema}>
        <div className={css.publicWrapper}>
          <nav className={css.publicNavBar}>
            <a href="/s">
              <img src={AppLogo} alt="Viveka" className={css.publicNavLogo} />
            </a>
            <div className={css.publicNavActions}>
              <a href="/login" className={css.publicLoginBtn}>Login</a>
              <a href="/signup/type" className={css.publicSignupBtn}>Sign Up</a>
            </div>
          </nav>
          {innerContent}
          <ContainerWrapper>
            <Footer />
          </ContainerWrapper>
        </div>
      </PageWrapper>
    );
  }
}

ProfilePageComponent.defaultProps = {
  currentUser: null,
  user: null,
  userShowError: null,
  queryListingsError: null,
  reviews: [],
  queryReviewsError: null,
};

const { bool, arrayOf, number, shape } = PropTypes;

ProfilePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: propTypes.currentUser,
  user: propTypes.user,
  userShowError: propTypes.error,
  queryListingsError: propTypes.error,
  listings: arrayOf(propTypes.listing).isRequired,
  reviews: arrayOf(propTypes.review),
  queryReviewsError: propTypes.error,
  onFetchMyTestimonials: PropTypes.func.isRequired,
  onFetchMyCredentials: PropTypes.func.isRequired,
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews,
    queryReviewsError,
  } = state.ProfilePage;

  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId, include: ['profile.metadata'] }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;
  const listings = getMarketplaceEntities(state, userListingRefs);

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    user,
    userShowError,
    queryListingsError,
    listings,
    reviews,
    queryReviewsError,
  };
};

const mapDispatchToProps = dispatch => ({
  onFetchMyTestimonials: () => dispatch(fetchMyTestimonials()),
  onFetchMyCredentials: () => dispatch(fetchMyCredentials()),
});

const ProfilePage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withViewport,
  injectIntl
)(ProfilePageComponent);

ProfilePage.loadData = params => {
  const id = new UUID(params.id);
  return loadData(id);
};

export default ProfilePage;
